import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { Loader2, LogIn } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  useFormField,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { PasswordInput } from '@/components/password-input'

const signInFormSchema = z.object({
  email: z
    .string()
    .email('auth:fields.email.errors.invalid_string')
    .max(256),
  password: z
    .string()
    .min(8, 'auth:fields.password.errors.too_small')
    .max(70, 'auth:fields.password.errors.too_big'),
})

function TranslatedFormMessage({
  className,
}: React.ComponentProps<'p'>) {
  const { t } = useTranslation()
  const { error, formMessageId } = useFormField()
  const body = error?.message ? t(error.message as string) : null
  if (!body) {
    return null
  }
  return (
    <p
      data-slot="form-message"
      id={formMessageId}
      className={cn('text-sm text-destructive', className)}
    >
      {body}
    </p>
  )
}

interface UserAuthFormProps extends React.HTMLAttributes<HTMLFormElement> {
  onSignIn: (payload: { email: string; password: string }) => Promise<void>
  isSigningIn: boolean
}

export function UserAuthForm({
  className,
  onSignIn,
  isSigningIn,
  ...props
}: UserAuthFormProps) {
  const { t } = useTranslation('auth')
  const form = useForm<z.infer<typeof signInFormSchema>>({
    resolver: zodResolver(signInFormSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  async function onSubmit(data: z.infer<typeof signInFormSchema>) {
    await onSignIn({ email: data.email, password: data.password })
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className={cn('grid gap-3', className)}
        {...props}
      >
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('fields.email.label')}</FormLabel>
              <FormControl>
                <Input placeholder="name@example.com" {...field} />
              </FormControl>
              <TranslatedFormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem className="relative">
              <FormLabel>{t('fields.password.label')}</FormLabel>
              <FormControl>
                <PasswordInput placeholder="********" {...field} />
              </FormControl>
              <TranslatedFormMessage />
            </FormItem>
          )}
        />
        <Button className="mt-2" type="submit" disabled={isSigningIn}>
          {isSigningIn ? <Loader2 className="animate-spin" /> : <LogIn />}
          {t('actions.signIn')}
        </Button>
      </form>
    </Form>
  )
}
